import type {WithBaselinePinnedFlags} from './types';
import {
  BackendLinesQueryResult,
  BackendLinesQueryRunsDelta,
  BucketedQueryResult,
  BucketedQueryRunsDelta,
} from './types';

/**
 * Merges baseline, pinned, and regular runs from aliased GraphQL queries.
 *
 * The query fetches:
 * - baselineRun: Single baseline run (via aliased runs query)
 * - pinnedRuns: Multiple pinned runs (via aliased runs query)
 * - runs: Regular runs (main query)
 *
 * All queries return data in delta format. This function merges them in order
 * (baseline → pinned → regular) and marks each run with isBaseline/isPinned flags.
 *
 * Generic to support both BucketedQueryResult and BackendLinesQueryResult.
 *
 * **Mutates the input result object** by modifying result.data.project.runs.deltas.
 */
export function mergeBaselineAndPinnedRuns<
  T extends BucketedQueryResult | BackendLinesQueryResult,
>(result: T): void {
  const project = result?.data?.project;
  if (!project) {
    return;
  }

  const baselineRunQuery = project.baselineRun;
  const pinnedRunsQuery = project.pinnedRuns;
  const mainRuns = project.runs;

  if (!mainRuns) {
    return;
  }

  type MergeableDelta = BucketedQueryRunsDelta | BackendLinesQueryRunsDelta;

  const createEmptyDeltas = (): NonNullable<typeof mainRuns.deltas> => ({
    __typename: 'BucketDiffs',
    delta: [],
    order: [],
  });

  let mainRunsDeltas = mainRuns.deltas;
  if (
    !mainRunsDeltas ||
    !Array.isArray(mainRunsDeltas.delta) ||
    !Array.isArray(mainRunsDeltas.order)
  ) {
    mainRunsDeltas = createEmptyDeltas();
    mainRuns.deltas = mainRunsDeltas;
  }

  const mainDeltas = mainRunsDeltas.delta as MergeableDelta[];
  const mainOrder = mainRunsDeltas.order;

  const baselineDeltas: ReadonlyArray<MergeableDelta> = (baselineRunQuery
    ?.deltas?.delta ?? []) as ReadonlyArray<MergeableDelta>;
  const baselineOrder = baselineRunQuery?.deltas?.order ?? [];
  const pinnedDeltas: ReadonlyArray<MergeableDelta> = (pinnedRunsQuery?.deltas
    ?.delta ?? []) as ReadonlyArray<MergeableDelta>;
  const pinnedOrder = pinnedRunsQuery?.deltas?.order ?? [];

  // Snapshot original deltas before clearing (needed for re-adding regular runs)
  const originalMainDeltas = [...mainDeltas];
  const originalMainOrder = [...mainOrder];

  // Create a set of baseline/pinned IDs for deduplication
  const baselineAndPinnedIds = new Set<string>();

  // Mutate mainDeltas and mainOrder arrays directly
  // Clear them first, then add merged data
  mainDeltas.length = 0;
  mainOrder.length = 0;

  // Add baseline run deltas
  for (const delta of baselineDeltas) {
    if (delta.run) {
      baselineAndPinnedIds.add(delta.run.id);
      // Mutate the run object to add flags (using WithBaselinePinnedFlags for type safety)
      const run = delta.run as WithBaselinePinnedFlags<typeof delta.run>;
      run.isBaseline = true;
      run.isPinned = false;
      mainDeltas.push(delta);
    }
  }
  mainOrder.push(...baselineOrder);

  // Add pinned runs deltas
  for (const delta of pinnedDeltas) {
    if (delta.run && !baselineAndPinnedIds.has(delta.run.id)) {
      baselineAndPinnedIds.add(delta.run.id);
      // Mutate the run object to add flags (using WithBaselinePinnedFlags for type safety)
      const run = delta.run as WithBaselinePinnedFlags<typeof delta.run>;
      run.isBaseline = false;
      run.isPinned = true;
      mainDeltas.push(delta);
    }
  }
  // Add pinned run IDs to order (dedupe against baseline only)
  for (const id of pinnedOrder) {
    if (!mainOrder.includes(id)) {
      mainOrder.push(id);
    }
  }

  // Add regular runs deltas (avoiding duplicates)
  for (const delta of originalMainDeltas) {
    if (delta.run && !baselineAndPinnedIds.has(delta.run.id)) {
      // Mutate the run object to add flags (using WithBaselinePinnedFlags for type safety)
      const run = delta.run as WithBaselinePinnedFlags<typeof delta.run>;
      run.isBaseline = false;
      run.isPinned = false;
      mainDeltas.push(delta);
    }
  }

  // Add regular run IDs to order (avoiding duplicates)
  for (const id of originalMainOrder) {
    if (!baselineAndPinnedIds.has(id)) {
      mainOrder.push(id);
    }
  }
}
